import React from 'react';

import { useForm, FormProvider } from 'react-hook-form';

import { useSelector } from 'react-redux';

import { zodResolver } from '@hookform/resolvers/zod';
import { dobZodField } from '@vvoyages/shared-ui/ui';
import { z } from 'zod';

import Button from '@/components/Button';
import { DateOfBirthField } from '@/components/DateOfBirthField';
import { WizardStep } from '@/components/Wizard';
import { appUiIds } from '@/constants/appUiIds';
import { DOB_VALIDATION, getDobValidationMessages } from '@/constants/dobValidation';
import useAppUiData from '@/hooks/useAppUiData';
import { selectResources } from '@/reducers/common/selectors';

import styles from './ClaimBookingStep.module.scss';

type ClaimBookingDobUpdateProps = {
  tabId: string;
  // eslint-disable-next-line no-unused-vars
  onNext: Function;
  onCancel: () => void;
  data: Record<string, any>;
};

const ClaimBookingDobUpdate: React.FC<ClaimBookingDobUpdateProps> = ({ tabId, onNext, onCancel, data }) => {
  const appUiData = useAppUiData();
  const resources = useSelector(selectResources);
  const dobValidation = getDobValidationMessages(resources, 'account');
  const sailingStartDate = data?.reservationDetails?.fromDate;

  const schema = z.object({
    dob: dobZodField({
      ...dobValidation,
      maxAge: DOB_VALIDATION.maxAge,
      serverISOTime: sailingStartDate,
    }),
  });

  const methods = useForm({
    resolver: zodResolver(schema),
    mode: 'all',
    defaultValues: { dob: '' },
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
  } = methods;

  const values = watch();
  const isFormFilled = Object.values(values).every(Boolean) && isValid;

  const onFormSubmit = handleSubmit(({ dob }) => {
    onNext({ ...data, selectedDOB: dob });
  });

  return (
    <WizardStep tabId={tabId}>
      <div className={styles.dobUpdateFormContent}>
        <h2 className={styles.updateDobTitle}>{appUiData[appUiIds.claimBooking.missingDobTitle]}</h2>
        <p className={styles.updateDobSubtext}>{appUiData[appUiIds.claimBooking.missingDobSubtext]}</p>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <FormProvider {...methods}>
          <form onSubmit={onFormSubmit} className={styles.formContainer} noValidate>
            <div className={styles.claimForm}>
              <DateOfBirthField
                name="dob"
                control={control}
                label="Date of Birth"
                required
                requiredMessage={dobValidation.requiredMessage}
                invalidYearMessage={dobValidation.invalidYearMessage}
                maxAge={DOB_VALIDATION.maxAge}
                serverISOTime={sailingStartDate}
                labelVariant="fieldGroup"
              />
              <Button type="submit" variant="primary" disabled={!isFormFilled} className={styles.dobClaimSubmit}>
                {appUiData[appUiIds.commonLabels.next]}
              </Button>
              <Button onClick={onCancel} className={styles.cancelLink}>
                {appUiData[appUiIds.commonLabels.cancel]}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </WizardStep>
  );
};

export default ClaimBookingDobUpdate;
